import React from "react";
import i18next from "i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "redux";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { FiCalendar } from "react-icons/fi";
import {
	Row,
	Col,
	FormGroup,
	FormLabel,
	FormControl,
	FormText,
} from "react-bootstrap";
import Switch from "components/Switch";
import { showModal, hideModal } from "actions/modalActions";
import Avatar from "components/Avatar";
import AvatarEdit from "components/Avatar/AvatarEdit";
import EmployeeInvitationLinkModal from "components/Employees/EmployeeInvitationLinkModal";
import { employeesService, widgetsService } from "services";
import DatePicker from "components/Shared/DatePicker";
import LocaleSelect from "components/Shared/LocaleSelect";
import {
	errorFormating,
	formatEmployeeSaveErrors,
	isSupervisor,
	isEmployeeCompanyFoundingAdmin,
} from "utils/utils";
import { validateSpanishID } from "utils/validation";
import { getAppName } from "utils/utils";
import { isHaddockEnvironment } from "utils/documentUtils";
import * as employeesActions from "actions/employeesActions";

/** La baja permanente pide confirmación solo cuando la fecha cambia de verdad. */
export const endDayChangeNeedsConfirm = (previousDay, nextDay) => {
	const previous = previousDay ? moment(previousDay).format("YYYY-MM-DD") : "";
	const next = nextDay ? moment(nextDay).format("YYYY-MM-DD") : "";
	return previous !== next;
};

export const shouldDisableOwnAdminEndDay = (employee, user) =>
	employee?.id === user?.id &&
	Array.isArray(user?.roles) &&
	user.roles.includes("ADMIN");

class EmployeeInfo extends React.Component {
	constructor(props) {
		super(props);

		this.state = this.buildStateFromEmployeeProps(props);

		this.closeModal = this.closeModal.bind(this);
		this.openAvatarModal = this.openAvatarModal.bind(this);
		this.openDeleteModal = this.openDeleteModal.bind(this);
		this.openInvitationLinkModal = this.openInvitationLinkModal.bind(this);
	}

	/** Copia el empleado desde props y alinea `role` con `roles` (sin mutar props). */
	buildStateFromEmployeeProps(props) {
		const employee = { ...props.employee };
		if (Array.isArray(employee.roles)) {
			const role = employee.roles.filter((item) => item !== "SUPERVISOR");
			employee.role = role.toString();
		}
		return {
			employee,
			prevEmployee: employee,
			isMe: props.employee.id === props.user?.id,
			errors: {},
			error_footer: "",
			modal_alert_baja: false,
		};
	}

	componentDidUpdate(prevProps) {
		if (
			this.props.employee?.id &&
			prevProps.employee?.id !== this.props.employee.id
		) {
			this.setState(this.buildStateFromEmployeeProps(this.props));
		}
	}

	/**
	 * La heurística del email bloqueado debe usar roles/fechas del último GET (props).
	 * El estado local puede conservar `roles` de otro empleado si hubo desincronización al navegar.
	 */
	getEmployeeForFoundingAdminCheck() {
		const p = this.props.employee;
		const s = this.state.employee;
		if (p?.id != null && s?.id != null && p.id === s.id) {
			return p;
		}
		return s;
	}

	closeModal() {
		this.props.hideModal();
	}

	openAvatarModal() {
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employee.editAvatar"),
				style: { width: "300px" },
				content: (
					<AvatarEdit
						userId={this.state.employee.id}
						onClose={this.closeModal}
					/>
				),
				closeModal: this.closeModal,
			},
			"empty"
		);
	}

	openDeleteModal() {
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employee.deleteEmployee"),
				style: { width: "400px" },
				message: i18next.t("employee.deleteConfirm"),
				deleteAction: this.confirmDelete,
				closeModal: this.closeModal,
			},
			"delete"
		);
	}

	handleSwitch = (event) => {
		const key = event.target.id;

		// No permitir desactivarse ni quitarse el acceso a uno mismo.
		if (this.state.isMe && (key === "enabled" || key === "with_access")) {
			return;
		}

		const { employee } = this.state;

		employee[key] = event.target.checked;

		if (!employee.enabled) employee.with_access = false;

		if (employee.with_access) employee.enabled = true;

		this.setState(
			{
				employee,
			},
			() => {
				this._saveEmployee();
			}
		);
	};

	handleRestrictedAction = () => {
		toastr.warning(
			i18next.t("employee.restrictedAction"),
			i18next.t("employee.restrictedActionMessage")
		);
	};

	handleChange = (event) => {
		const { employee } = this.state;

		const key = event.target.id;
		const value = event.target.value;

		// El rol propio se gestiona como en "Mi perfil": solo lectura.
		// Evita que un supervisor que abra su ficha desde un drawer pueda
		// promocionarse a administrador.
		if (key === "role" && this.state.isMe) {
			return;
		}

		if (
			key === "email" &&
			isEmployeeCompanyFoundingAdmin(
				this.getEmployeeForFoundingAdminCheck(),
				this.props.user?.company
			)
		) {
			return;
		}

		var modal_alert_baja = false;

		if (key === "end_day") {
			modal_alert_baja = true;
		}

		this.setState({
			prevEmployee: employee,
			employee: {
				...employee,
				[key]: value,
			},
			modal_alert_baja,
		});
	};

	handleDateChange = (id, date) => {
		const { employee } = this.state;

		const key = id;
		const value = date ? moment(date).format("YYYY-MM-DD") : "";

		if (key === "end_day") {
			if (shouldDisableOwnAdminEndDay(employee, this.props.user)) return;
			if (!endDayChangeNeedsConfirm(employee.end_day, value)) return;

			this.setState(
				{
					prevEmployee: employee,
					employee: {
						...employee,
						[key]: value,
					},
					modal_alert_baja: true,
				},
				() => this.openAlertBajaModal()
			);
			return;
		}

		this.setState(
			{
				prevEmployee: employee,
				employee: {
					...employee,
					[key]: value,
				},
				modal_alert_baja: false,
			},
			() => {
				// El resto de fechas se guardan al elegirlas. La baja permanente espera al popup.
				this._saveEmployee();
			}
		);
	};

	handleWidgetChange = async (event) => {
		const { employee } = this.state;

		const key = event.target.id;
		const value = event.target.value;
		var response;

		if (value > 0) {
			response = await widgetsService.addToEmployee(employee.id, value);
			// console.log(response);
		} else {
			response = await widgetsService.deleteFromEmployee(
				employee.id,
				employee.widget_template_id
			);
			// console.log(response);
		}

		if (response.ok) {
			this.setState({
				prevEmployee: employee,
				employee: {
					...employee,
					[key]: value,
				},
			});
			toastr.success(
				i18next.t("common.success"),
				i18next.t("profile.changesSaved")
			);
		}
	};

	handleBlur = async () => {
		const { modal_alert_baja } = this.state;
		const hasChanged = this.state.employee !== this.state.prevEmployee;

		if (hasChanged && modal_alert_baja) {
			this.openAlertBajaModal();
		} else if (hasChanged) {
			this._saveEmployee();
		}
	};

	openAlertBajaModal() {
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employee.alertBajaTitle"),
				message: i18next.t("employee.alertBajaMessage"),
				confirmAction: () => this.confirmChange("modal_alert_baja"),
				closeModal: () => this.undoChange("modal_alert_baja"),
				style: { width: "420px" },
			},
			"confirm"
		);
	}

	confirmChange = (modal_alert) => {
		this.setState({
			[modal_alert]: false,
		});
		this._saveEmployee();
		this.closeModal();
	};

	undoChange = (modal_alert) => {
		const { prevEmployee } = this.state;

		this.setState({
			employee: prevEmployee,
			[modal_alert]: false,
		});
		this.closeModal();
	};

	confirmDelete = async () => {
		const response = await employeesService.remove(this.state.employee.id);
		this.closeModal();

		if (response.status === 200) this.props.history.goBack();
		else {
			let textError = errorFormating(response);

			this.setState({
				error_footer: textError.id,
			});
		}
	};

	handleSendInvitation = async () => {
		const response = await employeesService.sendInvitation(
			this.state.employee.id
		);

		if (response.ok) {
			toastr.success("", i18next.t("employee.invitationSent"));
		} else {
			toastr.error("", i18next.t("employee.invitationError"));
		}
	};

	openInvitationLinkModal = () => {
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employee.invitationLink"),
				style: { width: "500px" },
				content: (
					<EmployeeInvitationLinkModal
						closeModal={this.closeModal}
						employee={this.state.employee}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	_saveEmployee = async () => {
		const { employee, prevEmployee } = this.state;

		const hasChangedRole = employee.role !== prevEmployee.role;
		// console.log(hasChangedRole);

		if (employee.nif && !validateSpanishID(employee.nif)) {
			toastr.error("Error", i18next.t("employee.invalidNif"));
			this.setState({
				errors: { ...this.state.errors, nif: i18next.t("employee.invalidNif") },
			});
			return;
		}

		if (hasChangedRole) {
			const roles = [];
			roles.push(employee.role);
			if (prevEmployee.roles.includes("SUPERVISOR")) {
				roles.push("SUPERVISOR");
			}
			employee.roles = roles;
		}

		// param role is sent, but only roles is used in backend
		const response = await employeesService.update(employee);

		if (response.ok) {
			toastr.success(
				i18next.t("common.success"),
				i18next.t("profile.changesSaved")
			);

			// Recargar el empleado completo desde el servidor para obtener todos los campos actualizados
			const updatedEmployeeResponse = await employeesService.getById(
				employee.id
			);
			if (updatedEmployeeResponse.ok) {
				const updatedEmployee = updatedEmployeeResponse;

				// Actualizar el estado local con el empleado completo actualizado
				this.setState({
					employee: updatedEmployee,
					prevEmployee: updatedEmployee,
					errors: {},
				});

				// Actualizar el empleado en el estado global
				this.props.updateEmployeeInState(updatedEmployee);

				// Notificar al componente padre que el empleado ha sido actualizado
				if (this.props.onEmployeeUpdated) {
					this.props.onEmployeeUpdated(updatedEmployee);
				}
			}
		} else {
			this.setState({
				errors: formatEmployeeSaveErrors(response),
			});
		}
	};

	checkValidField = (name) => {
		return (
			this.state.errors[name] !== undefined && this.state.errors[name] !== ""
		);
	};

	getSupervisedGroups = () =>
		this.props.employee.groups_supervised.map((group, i) => {
			return <li key={i}>{group.name}</li>;
		});

	getContent = () => {
		const { employee } = this.state;
		const { widget_templates } = this.props;
		var isActive = employee.enabled;
		var hasAccess = employee.with_access;
		var disableNotifications = employee.do_not_receive_notifications;
		let template_options =
			widget_templates.length > 0 &&
			widget_templates.map((item, i) => {
				return (
					<option key={i} value={item.id}>
						{item.name}
					</option>
				);
			}, this);
		const appName = getAppName();

		// Restricciones para usuarios Haddock: solo cuando se editan a sí mismos
		const isHaddockUser = isHaddockEnvironment();
		const shouldDisableForHaddock = isHaddockUser && this.state.isMe;
		const shouldDisableRole = this.state.isMe || shouldDisableForHaddock;
		const shouldDisableSelfAccountChanges = this.state.isMe;
		const isFoundingAdminEmailLocked = isEmployeeCompanyFoundingAdmin(
			this.getEmployeeForFoundingAdminCheck(),
			this.props.user?.company
		);
		const disableOwnAdminEndDay = shouldDisableOwnAdminEndDay(
			employee,
			this.props.user
		);

		return (
			<div className="employee-info-modern">
				{employee.pending_duplicated_email_confirmation && (
					<div className="alert alert-warning" role="alert">
						{i18next.t("employee.pendingDuplicatedEmailDisclaimer")}
					</div>
				)}
				{/* Datos Personales Section */}
				<div className="info-section">
					<h3 className="section-title">
						{i18next.t("employee.sectionPersonal")}
					</h3>
					<Row>
						<Col md={6}>
							<FormGroup className="form-group" controlId="name">
								<FormLabel>
									{i18next.t("employee.name")}{" "}
									<span className="label-required">*</span>
								</FormLabel>
								<FormControl
									type="text"
									value={employee.name}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.namePlaceholder")}
									isInvalid={this.checkValidField("name")}
									className="form-control-modern"
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.name}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup className="form-group" controlId="surname">
								<FormLabel>
									{i18next.t("employee.surname")}{" "}
									<span className="label-required">*</span>
								</FormLabel>
								<FormControl
									type="text"
									value={employee.surname}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.surnamePlaceholder")}
									isInvalid={this.checkValidField("surname")}
									className="form-control-modern"
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.surname}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup className="form-group" controlId="email">
								<FormLabel>
									{i18next.t("employee.email")}{" "}
									<span className="label-required">*</span>
								</FormLabel>
								<FormControl
									type="email"
									value={employee.email}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder="Email"
									isInvalid={this.checkValidField("email")}
									className="form-control-modern"
									disabled={isFoundingAdminEmailLocked}
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.email}
								</FormControl.Feedback>
								{isFoundingAdminEmailLocked && (
									<FormText muted>
										{i18next.t("employee.foundingAdminEmailLocked")}
									</FormText>
								)}
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup className="form-group" controlId="phone">
								<FormLabel>{i18next.t("employee.phone")}</FormLabel>
								<FormControl
									type="text"
									value={employee.phone}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.phonePlaceholder")}
									isInvalid={this.checkValidField("phone")}
									className="form-control-modern"
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.phone}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup
								controlId="birthday"
								className={`form-group form-date ${employee.birthday === "" && "empty"}`}
							>
								<FormLabel>{i18next.t("employee.birthday")}</FormLabel>
								<DatePicker
									selected={
										employee.birthday
											? moment(employee.birthday).toDate()
											: null
									}
									onChange={(date) => this.handleDateChange("birthday", date)}
									className="form-control"
									placeholderText="dd/mm/aaaa"
								/>
								<FiCalendar />
								<FormControl.Feedback type="invalid">
									{this.state.errors.birthday}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup className="form-group" controlId="nif">
								<FormLabel>{i18next.t("employee.nif")}</FormLabel>
								<FormControl
									type="text"
									value={employee.nif}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.nif")}
									className="form-control-modern"
									isInvalid={this.checkValidField("nif")}
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.nif}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup className="form-group" controlId="code">
								<FormLabel>{i18next.t("employee.code")}</FormLabel>
								<FormControl
									type="text"
									value={employee.code}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.codePlaceholder")}
									className="form-control-modern"
								/>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup className="form-group" controlId="card_code">
								<FormLabel>{i18next.t("employee.cardCode")}</FormLabel>
								<FormControl
									type="text"
									value={employee.card_code}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.cardCode")}
									className="form-control-modern"
								/>
							</FormGroup>
						</Col>
					</Row>
				</div>

				{/* Contratación Section */}
				<div className="info-section">
					<h3 className="section-title">
						{i18next.t("employee.sectionContract")}
					</h3>
					<Row>
						<Col md={4}>
							<FormGroup className="form-group" controlId="position">
								<FormLabel>{i18next.t("employee.position")}</FormLabel>
								<FormControl
									type="text"
									value={employee.position}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={i18next.t("employee.position")}
									className="form-control-modern"
									isInvalid={this.checkValidField("position")}
								/>
								<FormControl.Feedback type="invalid">
									{this.state.errors.position}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup
								controlId="start_day"
								className={`form-group form-date ${employee.start_day === "" && "empty"}`}
							>
								<FormLabel>{i18next.t("employee.contractDate")}</FormLabel>
								<DatePicker
									selected={
										employee.start_day
											? moment(employee.start_day).toDate()
											: null
									}
									onChange={(date) => this.handleDateChange("start_day", date)}
									className="form-control"
									placeholderText="dd/mm/aaaa"
								/>
								<FiCalendar />
								<FormControl.Feedback type="invalid">
									{this.state.errors.start_day}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={4}>
							<FormGroup
								controlId="end_day"
								className={`form-group form-date ${employee.end_day === "" && "empty"}`}
							>
								<FormLabel>{i18next.t("employee.endDay")}</FormLabel>
								<DatePicker
									selected={
										employee.end_day ? moment(employee.end_day).toDate() : null
									}
									onChange={(date) => this.handleDateChange("end_day", date)}
									className="form-control"
									placeholderText="dd/mm/aaaa"
									disabled={disableOwnAdminEndDay}
								/>
								<FiCalendar />
								<FormControl.Feedback type="invalid">
									{this.state.errors.end_day} {this.state.errors.contract_id}
								</FormControl.Feedback>
							</FormGroup>
						</Col>
					</Row>
				</div>

				{/* Configuración Plain Section */}
				<div className="info-section">
					<h3 className="section-title">
						{i18next.t("employee.sectionConfig", { appName })}
					</h3>
					<Row>
						<Col md={4}>
							<FormGroup className="form-group" controlId="role">
								<FormLabel>{i18next.t("employee.role")}</FormLabel>
								<div
									onClick={
										shouldDisableForHaddock
											? this.handleRestrictedAction
											: undefined
									}
								>
									<FormControl
										disabled={shouldDisableRole}
										onChange={this.handleChange}
										onBlur={this.handleBlur}
										value={employee.role}
										as="select"
										className="form-control-modern"
										isInvalid={this.checkValidField("role")}
									>
										<option value="">{i18next.t("employee.selectRole")}</option>
										<option value="ADMIN">
											{i18next.t("employee.roleAdmin")}
										</option>
										<option value="EMPLOYEE">
											{i18next.t("employee.roleEmployee")}
										</option>
									</FormControl>
								</div>
								<FormControl.Feedback type="invalid">
									{this.state.errors.role}
								</FormControl.Feedback>
							</FormGroup>
						</Col>
						<Col md={4}>
							{widget_templates && widget_templates.length > 0 && (
								<FormGroup
									className="form-group"
									controlId="widget_template_id"
								>
									<FormLabel>{i18next.t("employee.startTemplate")}</FormLabel>
									<FormControl
										onChange={this.handleWidgetChange}
										value={employee.widget_template_id}
										as="select"
										className="form-control-modern"
										isInvalid={this.checkValidField("widget_template_id")}
									>
										<option value="">
											{i18next.t("employee.selectTemplate")}
										</option>
										{template_options}
									</FormControl>
									<FormControl.Feedback type="invalid">
										{this.state.errors.widget_template_id}
									</FormControl.Feedback>
								</FormGroup>
							)}
						</Col>
						<Col md={4}>
							<FormGroup
								className="form-group"
								controlId="time_registration_balance_calculation"
							>
								<FormLabel>
									{i18next.t("employee.balanceCalculation")}
								</FormLabel>
								<FormControl
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									value={employee.time_registration_balance_calculation}
									as="select"
									className="form-control-modern"
									isInvalid={this.checkValidField(
										"time_registration_balance_calculation"
									)}
								>
									<option value="PLANNED_HOURS">
										{i18next.t("times.balance.plannedHours")}
									</option>
									<option value="TARGET_HOURS">
										{i18next.t("times.balance.targetHours")}
									</option>
								</FormControl>
								<FormControl.Feedback type="invalid">
									{this.state.errors.time_registration_balance_calculation}
								</FormControl.Feedback>
							</FormGroup>
						</Col>
						<Col md={4}>
							<FormGroup className="form-group" controlId="locale">
								<FormLabel>{i18next.t("employee.locale")}</FormLabel>
								<LocaleSelect
									value={employee.locale || "es"}
									onChange={(val) => {
										this.setState(
											(prev) => ({
												employee: { ...prev.employee, locale: val },
											}),
											this.handleBlur
										);
									}}
								/>
							</FormGroup>
						</Col>
					</Row>
					<Col sm={12} md={6} lg={4} className="p-0">
						<div className="switch-section-new">
							<FormGroup
								controlId="enabled"
								className="form-group switch-group-new"
							>
								<FormLabel>{i18next.t("employee.active")}</FormLabel>
								<div
									onClick={
										shouldDisableForHaddock
											? this.handleRestrictedAction
											: undefined
									}
								>
									<Switch
										controlId="enabled"
										isOn={isActive}
										handleToggle={this.handleSwitch}
										disabled={
											shouldDisableForHaddock || shouldDisableSelfAccountChanges
										}
									/>
								</div>
							</FormGroup>

							<FormGroup
								controlId="with_access"
								className="form-group switch-group-new"
							>
								<FormLabel>
									{i18next.t("employee.access", { appName })}
								</FormLabel>
								<div
									onClick={
										shouldDisableForHaddock
											? this.handleRestrictedAction
											: undefined
									}
								>
									<Switch
										controlId="with_access"
										isOn={hasAccess}
										handleToggle={this.handleSwitch}
										disabled={
											shouldDisableForHaddock || shouldDisableSelfAccountChanges
										}
									/>
								</div>
							</FormGroup>

							<FormGroup
								controlId="do_not_receive_notifications"
								className="form-group switch-group-new"
							>
								<FormLabel>
									{i18next.t("employee.noNotifications", { appName })}
								</FormLabel>
								<Switch
									controlId="do_not_receive_notifications"
									isOn={disableNotifications}
									handleToggle={this.handleSwitch}
								/>
							</FormGroup>
						</div>
					</Col>
				</div>

				{/* Supervised Groups */}
				{employee.groups_supervised.length > 0 && (
					<div className="info-section">
						<h3 className="section-title">
							{i18next.t("employee.sectionSupervises")}
						</h3>
						<div className="supervised-groups">
							<ul className="list-supervised">{this.getSupervisedGroups()}</ul>
						</div>
					</div>
				)}

				{/* Error Messages */}
				{this.state.error_footer && (
					<div
						className="alert alert-danger alert-dismissible fade show mt15"
						role="alert"
					>
						{this.state.error_footer}
					</div>
				)}

				{this.state.errors && this.state.errors.general && (
					<div
						className="alert alert-danger alert-dismissible fade show mt15"
						role="alert"
					>
						{this.state.errors.general}
					</div>
				)}

				{/* Action Buttons */}
				{!this.state.isMe && (
					<div className="row-buttons">
						<button
							type="button"
							className="btn btn-danger"
							onClick={this.openDeleteModal}
						>
							{i18next.t("employee.deleteEmployee")}
						</button>

						{isActive && hasAccess && !employee.last_login && (
							<button
								type="button"
								className="btn btn-outline-primary"
								onClick={this.openInvitationLinkModal}
							>
								{i18next.t("employee.invitationLink")}
							</button>
						)}

						{hasAccess && (
							<button
								type="button"
								className="btn btn-outline-primary"
								onClick={this.handleSendInvitation}
							>
								{i18next.t("employee.resendAccessEmail", { appName })}
							</button>
						)}
					</div>
				)}
			</div>
		);
	};

	render() {
		return <>{this.getContent()}</>;
	}
}

const mapStateToProps = (state) => {
	return {
		...state.dashboardReducer,
		user: state.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	updateEmployeeInState: (employee) =>
		dispatch(employeesActions.updateEmployeeInState(employee)),
});

// export default connect(mapStateToProps, mapDispatchToProps)(EmployeeInfo);
export default compose(
	withRouter,
	connect(mapStateToProps, mapDispatchToProps)
)(EmployeeInfo);
